import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

import {
  Container,
  Header,
  HeaderContent,
  CirculoCarregando,
  CirculoCarregandoDiv,
  BoxCadastro,
  Content,
  GroupBox,
  GroupBoxLegenda,
  InputDescricao,
  InputGrupo,
  InputUm,
  InputPreco,
  CheckboxDestaque,
  PesquisaButton,
  InputDescricaoPesq,
  InputGrupoPesq,
  ContainerPesquisa,
  BotaoSalvar,
  CardProduto,
  CardDestaque,
  InputCusto,
  InputNcm,
} from './styles';

import { useConfigs } from '../../context/ConfigsContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';

import logoImg from '../../assets/logo-sizex2.jpg';
import { FiSave, FiSearch, FiStar } from 'react-icons/fi';

interface Produto {
  id: string;
  empresa_id: string;
  descricao: string;
  um: string;
  preco: number;
  destaque: 'S' | 'N';
  grupo: string;
}

const Produto: React.FC = () => {
  const { cores, lerConfig } = useConfigs();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [logoDinamico, setLogoDinamico] = useState<string | undefined>(
    undefined,
  );
  const [carregarComDelay, setCarregarComDelay] = useState(false);

  // States para o form simples
  const [descricao, setDescricao] = useState<string>('');
  const [descricaoErro, setDescricaoErro] = useState<string>('');
  const [grupo, setGrupo] = useState<string>('');
  const [grupoErro, setGrupoErro] = useState<string>('');
  const [um, setUm] = useState<string>('');
  const [umErro, setUmErro] = useState<string>('');
  const [preco, setPreco] = useState<string>('');
  const [precoErro, setPrecoErro] = useState<string>('');
  const [destaque, setDestaque] = useState<boolean>(false);
  const [ncm, setNcm] = useState<string>('');
  const [custo, setCusto] = useState<string>('');

  // Refs para o cadastro
  const inputDescricao = useRef<HTMLInputElement>(null);
  const inputGrupo = useRef<HTMLInputElement>(null);
  const inputUm = useRef<HTMLInputElement>(null);
  const inputPreco = useRef<HTMLInputElement>(null);

  // Pesquisa
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [produtosFiltrados, setProdutosFiltrados] = useState<Produto[]>([]);
  const [descricaoPesq, setDescricaoPesq] = useState<string>('');
  const [grupoPesq, setGrupoPesq] = useState<string>('');

  useEffect(() => {
    // IMAGENS DINÂMICAS
    let configLogo: string | undefined = lerConfig('LOGO');
    if (configLogo && configLogo !== '') {
      api
        .get<string>('/empresas/configs-caminho-arquivo', {
          params: {
            arquivo: configLogo,
          },
        })
        .then(response => {
          setLogoDinamico(response.data);
        });
    } else {
      configLogo = undefined;
    }

    // Delay pra dar tempo de carregar as cores do sistema quando não utiliza login
    setTimeout(() => {
      setCarregarComDelay(true);
    }, 200);
  }, [lerConfig, user.empresa.url_frontend]);

  const atualiza = useCallback(async () => {
    const produtosResponse = await api.get<Produto[]>('/produtos');

    setProdutos(produtosResponse.data);
  }, []);

  const validacao = useCallback(() => {
    setDescricaoErro('');
    setGrupoErro('');
    setUmErro('');
    setPrecoErro('');

    let erroValidacao = false;
    if (descricao === '') {
      setDescricaoErro('Informação obrigatória!');
      erroValidacao = true;
    } else {
      if (descricao.length > 120) {
        addToast({
          type: 'error',
          title: 'Pendências na validação',
          description: 'Tamanho máximo do campo Descrição é 120 caracteres.',
        });
        return false;
      }
    }
    if (grupo === '') {
      setGrupoErro('Informação obrigatória!');
      erroValidacao = true;
    } else {
      if (grupo.length > 50) {
        addToast({
          type: 'error',
          title: 'Pendências na validação',
          description: 'Tamanho máximo do campo Grupo é 50 caracteres.',
        });
        return false;
      }
    }
    if (um === '') {
      setUmErro('Informação obrigatória!');
      erroValidacao = true;
    } else {
      if (um.length > 6) {
        addToast({
          type: 'error',
          title: 'Pendências na validação',
          description: 'Tamanho máximo do campo U.M. é 6 caracteres.',
        });
        return false;
      }
    }

    if (preco === '') {
      setPrecoErro('Informação obrigatória!');
      erroValidacao = true;
    }

    if (erroValidacao) {
      addToast({
        type: 'info',
        title: 'Pendências na validação',
        description:
          'Efetue as correções indicadas em cada campo e tente novamente.',
      });
      return false;
    }
  }, [addToast, descricao, grupo, um, preco]);

  const limpa = useCallback(() => {
    setDescricao('');
    setGrupo('');
    setUm('');
    setNcm('');
    setCusto('');
    setPreco('');
    setDestaque(false);
  }, []);

  const pesquisa = useCallback(() => {
    let listaFiltrar = produtos;

    if (descricaoPesq.length < 3 && grupoPesq.length < 3) {
      addToast({
        type: 'error',
        title: 'Digite ao menos 3 caracteres para filtrar',
      });

      return;
    }

    if (descricaoPesq !== '') {
      listaFiltrar = listaFiltrar?.filter(produto =>
        produto.descricao.toUpperCase().includes(descricaoPesq.toUpperCase()),
      );
    }

    if (grupoPesq !== '') {
      listaFiltrar = listaFiltrar?.filter(produto =>
        produto.grupo.toUpperCase().includes(grupoPesq.toUpperCase()),
      );
    }

    setProdutosFiltrados(listaFiltrar);
  }, [descricaoPesq, grupoPesq, produtos, addToast]);

  const aoSalvar = useCallback(async () => {
    if (validacao() === false) {
      return;
    }

    try {
      const produto = {
        descricao,
        um,
        grupo,
        ncm,
        preco: Number(preco.replace(',', '.')),
        custo: Number(custo.replace(',', '.')),
        destaque: destaque ? 'S' : 'N',
      };

      await api.post('/produtos/web', produto);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro iterno. Tente novamente em instantes',
      });

      return;
    }

    atualiza();
    limpa();

    addToast({
      type: 'success',
      title: 'Produto salvo com sucesso!!!',
    });

    //history.push('/home');
  }, [
    descricao,
    destaque,
    grupo,
    preco,
    um,
    ncm,
    custo,
    validacao,
    addToast,
    atualiza,
    limpa,
  ]);

  useEffect(() => {
    atualiza();
  }, [atualiza]);

  const aoAlterarDescricao = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDescricao(event.target.value);
      setDescricaoErro('');
    },
    [],
  );

  const aoAlterarDescricaoPesq = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDescricaoPesq(event.target.value);
    },
    [],
  );

  const aoAlterarGrupo = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setGrupo(event.target.value);
      setGrupoErro('');
    },
    [],
  );

  const aoAlterarGrupoPesq = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setGrupoPesq(event.target.value);
    },
    [],
  );

  const aoAlterarNcm = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNcm(event.target.value);
    },
    [],
  );

  const aoAlterarCusto = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCusto(event.target.value);
    },
    [],
  );

  const aoAlterarUm = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUm(event.target.value);
      setUmErro('');
    },
    [],
  );

  const aoAlterarPreco = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPreco(event.target.value);
      setPrecoErro('');
    },
    [],
  );

  return carregarComDelay ? (
    <Container cores={cores}>
      <Header cores={cores}>
        <HeaderContent cores={cores}>
          <Link to="/home">
            <img src={logoDinamico ? logoDinamico : logoImg} alt="Logo" />
          </Link>
        </HeaderContent>
      </Header>

      <Content>
        <BoxCadastro>
          <GroupBox cores={cores}>
            <GroupBoxLegenda>Inclusão de produtos</GroupBoxLegenda>

            <span>
              <InputDescricao
                variant="outlined"
                margin="normal"
                id="desc"
                label="Descrição"
                name="descricao"
                inputRef={inputDescricao}
                value={descricao}
                onChange={aoAlterarDescricao}
                error={descricaoErro !== ''}
                helperText={descricaoErro}
              />

              <InputGrupo
                variant="outlined"
                margin="normal"
                id="grupo"
                label="Grupo"
                name="grupo"
                inputRef={inputGrupo}
                value={grupo}
                onChange={aoAlterarGrupo}
                error={grupoErro !== ''}
                helperText={grupoErro}
              />
            </span>

            <span>
              <InputNcm
                variant="outlined"
                margin="normal"
                id="ncm"
                label="NCM"
                name="ncm"
                value={ncm}
                onChange={aoAlterarNcm}
              />

              <InputUm
                variant="outlined"
                margin="normal"
                id="um"
                label="U.M."
                name="um"
                inputRef={inputUm}
                value={um}
                onChange={aoAlterarUm}
                error={umErro !== ''}
                helperText={umErro}
              />

              <InputCusto
                variant="outlined"
                margin="normal"
                id="custo"
                label="Custo"
                name="custo"
                value={custo}
                onChange={aoAlterarCusto}
              />

              <InputPreco
                variant="outlined"
                margin="normal"
                id="preco"
                label="Preço"
                name="preco"
                inputRef={inputPreco}
                value={preco}
                onChange={aoAlterarPreco}
                error={precoErro !== ''}
                helperText={precoErro}
              />

              <CheckboxDestaque cores={cores}>
                <input
                  type="checkbox"
                  checked={destaque}
                  onChange={() => {
                    setDestaque(!destaque);
                  }}
                />
                Destaque
              </CheckboxDestaque>
            </span>

            <BotaoSalvar cores={cores}>
              <button type="button" onClick={aoSalvar}>
                <div>
                  <FiSave />
                  <span>SALVAR</span>
                </div>
              </button>
            </BotaoSalvar>

            <h3>Pesquisa</h3>

            <span>
              <InputDescricaoPesq
                variant="outlined"
                margin="normal"
                id="descPesq"
                label="Descrição"
                name="descricaoPesq"
                value={descricaoPesq}
                onChange={aoAlterarDescricaoPesq}
              />

              <InputGrupoPesq
                variant="outlined"
                margin="normal"
                id="grupoPesq"
                label="Grupo"
                name="grupoPesq"
                value={grupoPesq}
                onChange={aoAlterarGrupoPesq}
              />

              <PesquisaButton cores={cores} onClick={pesquisa}>
                <FiSearch />
              </PesquisaButton>
            </span>

            <ContainerPesquisa>
              {produtosFiltrados.map(produto => (
                <CardProduto key={produto.id} cores={cores}>
                  <h4>{produto.descricao}</h4>
                  <p>{produto.grupo}</p>

                  <CardDestaque destaque={produto.destaque === 'S'}>
                    <FiStar />
                  </CardDestaque>
                </CardProduto>
              ))}
            </ContainerPesquisa>
          </GroupBox>
        </BoxCadastro>
      </Content>
    </Container>
  ) : (
    <CirculoCarregandoDiv>
      <CirculoCarregando cores={cores} />
    </CirculoCarregandoDiv>
  );
};

export default Produto;
